/* Browser-side photo compression: big camera JPEG/HEIC → ≤1920px JPEG so uploads finish on slow mobile data. */
import { warn } from "@/lib/log";

const MAX_SIDE = 1920;
const QUALITY = 0.85;
const SKIP_BELOW_BYTES = 600 * 1024;

const isHeic = (f) => /hei[cf]/i.test(f.type || "") || /\.(heic|heif)$/i.test(f.name || "");
export const isImageFile = (f) => /^image\//i.test(f.type || "") || /\.(heic|heif|jpe?g|png|webp)$/i.test(f.name || "");
export const isPdfFile = (f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name || "");

async function decode(file) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file, { imageOrientation: "from-image" }); }
    catch (err) { warn("createImageBitmap failed, falling back to <img> decode", err?.message || err); }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
    img.src = url;
  });
}

const toBlob = (canvas) => new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", QUALITY));

export async function compressImage(file, { maxSide = MAX_SIDE } = {}) {
  if (!isImageFile(file)) return file;
  const heic = isHeic(file);
  if (!heic && file.size < SKIP_BELOW_BYTES) return file;
  let bmp;
  try { bmp = await decode(file); } catch {
    throw new Error(heic
      ? "HEIC photo can't be read on this device — in Camera settings choose “Most compatible” (JPEG) and retake."
      : "This image could not be read. Please retake the photo.");
  }
  const w = bmp.width || bmp.naturalWidth, h = bmp.height || bmp.naturalHeight;
  const scale = Math.min(1, maxSide / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
  if (typeof bmp.close === "function") bmp.close();
  const blob = await toBlob(canvas);
  if (!blob) throw new Error("Could not compress the photo. Please try again.");
  if (!heic && blob.size >= file.size) return file;
  const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
  return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
}
